import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { LoadingController, Platform } from "@ionic/angular";
import { Navigator } from "src/app/components/menu/navigator";
import {
  GeoComplyAppStores,
  GeoComplyStatus,
  GeoResponse,
} from "src/app/interfaces/geo-interface";
import { AppLinksService } from "src/app/services/applinks/app-links.service";
import { isUndefined } from "src/app/services/codeUtils";
import { ConfigService } from "src/app/services/config/config.service";
import { EventsService } from "src/app/services/event/event.service";
import { GeoComplyOobeeService } from "src/app/services/geo/geo-comply-oobee.service";
import { JurisdictionService } from "src/app/services/geo/jurisdiction.service";
import { UtilsService } from "src/app/services/utils/utils.service";

@Component({
  selector: "app-geo-comply",
  templateUrl: "./geo-comply.component.html",
  styleUrls: ["./geo-comply.component.scss"],
})
export class GeoComplyComponent implements OnInit, OnDestroy {
  @Input() public params: any;

  public alignTextLeft: boolean = false;
  public androidStoreLink: boolean = false;
  public downloadLink: string;
  public showValidatorDownload = false;
  public jurisdictionSubscription: any;
  public errorMessage: string;
  public error_messages: any;
  public error_details: any;
  public troubleshooter_message: string;
  public troubleshooter_fallback_message: string;
  public friendlyGeoComplyCodes: any;
  public funcToRetry: (g: GeoResponse) => any = null;
  public geoComplyStatus: GeoComplyStatus;
  public iosStoreLink: boolean = false;
  public installationVerified: boolean = false;
  public isRunning: boolean;
  public myInterval: any;
  public pageNeedsReload: boolean = false;
  public stateName: string = this.config.getState();
  public oobeeInteraction: boolean = false;
  public verifyLoader: any;

  public defaultLocationText: any = `
  <p class="ion-text-center">To help us locate you, please make sure your Wi-Fi and Location are enabled.</p>
  <p class="ion-text-left"><br/><br/>1. Go to Settings > Network & internet > click "Internet" > switch on "Wi-Fi" (Note: It is not necessary to connect to a Wi-Fi network for us to locate you. Your device just needs to have Wi-Fi enabled, and the ability to detect other networks). 
  <br/>2. Make sure that your device can detect multiple Wi-Fi networks. Go to Settings > Network & internet > click "Internet" > check under "Wi-Fi" to make sure other networks are detected.
  <br/>3. Go to Settings > Location > switch on "Use Location".
  <br/>4. Go to Settings > Location > select "App location permissions" > select the app you are using to wager > select "Allow only while using the app" or "Allow all the time" > switch on "Use precise location".
  <br/>5. Go to Settings > Location > Location Services > switch on "Google Location Accuracy", "Wi-Fi scanning", and "Bluetooth scanning".</p>`;

  constructor(
    public appLinks: AppLinksService,
    public config: ConfigService,
    public events: EventsService,
    public geoComplyOobeeService: GeoComplyOobeeService,
    public loadingController: LoadingController,
    public navigator: Navigator,
    public platform: Platform,
    public utils: UtilsService,
    protected jurisdiction: JurisdictionService
  ) { }

  ngOnInit() {
    this.supressErrorDialogue();
    if (this.jurisdictionSubscription) {
      this.jurisdictionSubscription.unsubscribe();
    }

    this.jurisdictionSubscription = this.jurisdiction.listen().subscribe((geoResp: GeoResponse) => {

      if (geoResp?.error?.error_code == "INTERACTION") {
        this.oobeeInteraction = true
      }
    });
  }

  ionViewDidLeave(): void {
    this.events.publish("event:geoComply:EnableErrorDialog");
  }

  ngAfterContentInit() {
    this.initializeGeoComply();
  }

  initializeGeoComply() {
    this.params = this.params ?? {};

    this.friendlyGeoComplyCodes = {
      boundary:
        "<p>We have detected that you are attempting to wager from outside the state of " +
        this.stateName +
        ". You should immediately cease and desist from attempting to wager from outside of " +
        this.stateName +
        ".We have retained your user information and future attempts could result in an enforcement action.</p>",
      timeout:
        "<p>Due to connection issue geolocation could not be run at this time.  Please try again later.",
      proxy: "For security purposes, you are requied to turn off your proxy or IP anonymizer. Please disable, then try again.",
      612: "Please download and install the player location check software.",
      615: "<p>Current Player Location plug-in is out of date.</p><p>Please download and install the Player location check software.</p>",
      614: "<p>The previous request for the player's location was interrupted.</p><p>Please wait.</p>",
      803: "<p>You have previously denied location access for this site.</p><p>Please set the Location access to \'Allowed\' in your browser settings.</p>",
      adb_connection:
        "<p>For Security purposes, you are required to remove ADB (Android Debug Bridge) connection. Please disable, then try again.</p>",
      min_loc_number: `<p>More location data is needed to confirm you're in a permitted area. Mobile data connection: Please make sure "High Accuracy" Location Services are enabled. WIFI connection: make sure your device is in range of multiple wireless connections. Please address items above, then try again.</p>`,
    };

    this.events.publish("event:geoComply:dismiss");

    this.processErrorMessaging();
  }

  private processErrorMessaging(eGeoComplyStatus?: any) {
    if (this.params["errorMessage"]) {
      this.errorMessage = this.params["errorMessage"];
      return console.error(this.errorMessage);
    }

    this.geoComplyStatus = eGeoComplyStatus ?? this.params["geoComplyStatus"];
    if (this.geoComplyStatus?.error_details) {
      const xmlDoc = this.parseXml(this.geoComplyStatus?.error_details);
      const boundaryElement = xmlDoc.querySelector("boundary");
      const proxyElement = xmlDoc.querySelector("proxy");
      const xmlTroubleShootMsg = this.parseXml(this.geoComplyStatus.troubleshooter_message);

      if (this.config.getConfig().FEATURE_DISPLAY_GEO_TROUBLESHOOTER_MSG === '1') {
        if (xmlTroubleShootMsg) {
          const messageValue = xmlTroubleShootMsg.getElementsByTagName("message")[0].childNodes[0].nodeValue;
          this.troubleshooter_message = "";
          this.errorMessage = "";
          let messageValue2 = "";
          let messageValue3 = "";
          let messageValue4 = "";
          if (messageValue) {
            this.troubleshooter_message = messageValue;
            this.errorMessage = this.troubleshooter_message;
            //Add further messages if it's avaliable to client.
            if (xmlTroubleShootMsg.getElementsByTagName("message").length > 1) {
              messageValue2 = xmlTroubleShootMsg.getElementsByTagName("message")[1].childNodes[0].nodeValue;
              this.errorMessage += '<br/>' + messageValue2;
              if (xmlTroubleShootMsg.getElementsByTagName("message")[2]) messageValue3 = xmlTroubleShootMsg.getElementsByTagName("message")[2].childNodes[0].nodeValue;
              this.errorMessage += '<br/>' + messageValue3;
              if (xmlTroubleShootMsg.getElementsByTagName("message")[3]) messageValue4 = xmlTroubleShootMsg.getElementsByTagName("message")[3].childNodes[0].nodeValue;
              this.errorMessage += '<br/>' + messageValue4;
            }
            return;
          }
          console.log(this.troubleshooter_message);

        } else {
          //fallback if troubleshooter_message is not returned 
          this.troubleshooter_fallback_message = this.config.getConfig().FEATURE_DISPLAY_GEO_TROUBLESHOOTER_FALLBACK_MSG ? this.config.getConfig().FEATURE_DISPLAY_GEO_TROUBLESHOOTER_FALLBACK_MSG : "Sorry, something went wrong. We couldn't determine your current location.";
          this.errorMessage = this.troubleshooter_fallback_message;
          return;
        }
      }

        if (boundaryElement) {
          this.error_details = {
            description: boundaryElement.getAttribute("description"),
            type: boundaryElement.getAttribute("type"),
          };
        }

        if (proxyElement) {
          this.error_details = {
            name: proxyElement.getAttribute("name"),
            type: proxyElement.getAttribute("type"),
          };
        }
      }

    if (this.geoComplyStatus) {
      this.error_messages =
        "string" === typeof this.geoComplyStatus.error_message
          ? this.geoComplyStatus.error_message.split(",")
          : [];

      const errorCode = this.geoComplyStatus.error_code;

      if (this.geoComplyStatus.error_code === 103) {
        this.pageNeedsReload = false;
        this.errorMessage = null;
        this.installationVerified = false;
        this.checkForLinks();
        return;
      }

      if (
        this.error_details &&
        this.error_details.description == "No coordinates were provided" &&
        this.error_details.type == "no_data"
      ) {
        this.alignTextLeft = true;
        this.errorMessage = this.defaultLocationText;
        return;
      }
      if (
        this.error_details &&
        this.error_details.name &&
        this.error_details.type &&
        this.geoComplyStatus.error_message == "proxy"
      ) {
        this.alignTextLeft = true;
        this.errorMessage = `For security purposes, you're required to disable the VPN software.`;
        return;
      }

      if (
        this.geoComplyStatus.error_code === 612 ||
        this.geoComplyStatus.error_code === 615
      ) {
        this.pageNeedsReload = false;
        const config: any = this.config.getConfig();
        if (navigator.appVersion.indexOf("Win") !== -1) {
          this.downloadLink =
            config.FEATURE_GEOCOMPLY_DOWNLOAD_URL +
            "?id=" +
            config.FEATURE_GEOCOMPLY_OPERATOR_ID +
            "&os=win&version=" +
            config.FEATURE_GEOCOMPLY_CLIENT_VERSION;
          return;
        }
        if (
          /Macintosh/.test(window.navigator.userAgent) &&
          !this.platform.is("ipad")
        ) {
          this.downloadLink =
            config.FEATURE_GEOCOMPLY_DOWNLOAD_URL +
            "?id=" +
            config.FEATURE_GEOCOMPLY_OPERATOR_ID +
            "&os=mac&version=" +
            config.FEATURE_GEOCOMPLY_CLIENT_VERSION;
        }

        return;
      }

      if (errorCode === 611) {
        this.alignTextLeft = true;
        this.errorMessage = this.defaultLocationText;
        return;
      }

      if (this.error_messages.indexOf("blocked_software") > -1) {
        let xmlObj = this.parseXml(this.geoComplyStatus.error_details);
        this.errorMessage = `<p>Remote Desktop Software Detected</p><p>For security purposes, you're required to disable prohibited software.</p>`;
        var softwares: any = xmlObj.getElementsByTagName("software");
        var programNames: Set<string> = new Set();

        for (var software of softwares) {
          programNames.add(software.getAttribute("display_name"));
        }

        for (var programName of Array.from(programNames.values())) {
          this.errorMessage += `<p>Please disable ${programName}, then try again.</p>`;
        }
        return;
      }
      //
      if (this.error_messages.indexOf("blocked_software") > -1) {
        let xmlObj = this.parseXml(this.geoComplyStatus.error_details);
        this.errorMessage = `<p>Remote Desktop Software Detected</p><p>For security purposes, you're required to disable prohibited software.</p>`;
        var softwares: any = xmlObj.getElementsByTagName("software");
        var programNames: Set<string> = new Set();

        for (var software of softwares) {
          programNames.add(software.getAttribute("display_name"));
        }

        for (var programName of Array.from(programNames.values())) {
          this.errorMessage += `<p>Please disable ${programName}, then try again.</p>`;
        }
        return;
      }

      if (this.error_messages.indexOf("virtual_machine_validation") > -1) {
        this.alignTextLeft = true;
        this.errorMessage = this.defaultLocationText;
        return;
      }

      /**
       * Oobee 3.5 INTERACTION requests:
       */
      if (this.geoComplyStatus.error_code === "INTERACTION") {
        this.oobeeInteraction = true;
        return;
      }
      /**
       * Oobee 3.5 INTERACTION end.
       */

      if (
        this.geoComplyStatus.error_code &&
        "undefined" !==
        typeof this.friendlyGeoComplyCodes[this.geoComplyStatus.error_code]
      ) {
        friendlyMessage =
          this.friendlyGeoComplyCodes[this.geoComplyStatus.error_code];
        if (isUndefined(this.errorMessage))
          return (this.errorMessage = `<p>${friendlyMessage}</p>`);
        return (this.errorMessage += `<p>${friendlyMessage}</p>`);
      }

      if (this.geoComplyStatus.error_message) {
        var messages = this.geoComplyStatus.error_message.split(",");
        for (var m in messages) {
          var friendlyMessage = "";
          this.errorMessage = "";
          var errorKey = messages[m];
          if ("undefined" !== typeof this.friendlyGeoComplyCodes[errorKey]) {
            friendlyMessage = this.friendlyGeoComplyCodes[errorKey];
          } else {
            // friendlyMessage = `<p>${errorKey}</p>` + this.friendlyGeoComplyCodes.boundary;
            // we don't want to show Geo response "key"
            friendlyMessage = this.friendlyGeoComplyCodes.boundary;
          }
          this.installationVerified = true;
          this.downloadLink = null;
          this.errorMessage += `${friendlyMessage}`;
        }
        return;
      }
    }
  }

  public downloadGeoGaurd(destination: string) {
    const applink: string = this.getGeoGaurdLink();
    return (window.location.href = applink ?? destination);
  }

  private getGeoGaurdLink() {
    if (this.platform.is("android"))
      return (
        this.config
          .getExternalLinks()
          .payload.filter((link) => link?.code == "oobee_applink")[0]?.url ??
        "https://play.google.com/store/apps/details?id=com.geocomply.oobeeapp&hl=en"
      );
    if (this.platform.is("ios"))
      return (
        this.config
          .getExternalLinks()
          .payload.filter((link) => link?.code == "oobee_applink_ios")[0]
          ?.url ?? "https://itunes.apple.com/us/app/oobee/id921993633"
      );
  }

  private checkForLinks() {
    if (this.platform.is("android")) {
      this.androidStoreLink = true;
      this.iosStoreLink = false;
      return;
    }

    if (this.platform.is("ios")) {
      this.iosStoreLink = true;
      this.androidStoreLink = false;
      return;
    }
  }

  private parseXml(xmlStr) {
    return new window.DOMParser().parseFromString(xmlStr, "text/xml");
  }

  public onDownload() {
    return this.appLinks.openExternalPage(this.downloadLink);
  }

  public onLaunchGooglePlayStore() {
    return window.open(GeoComplyAppStores.android);
  }

  public onLaunchIOSAppStore() {
    if (this.geoComplyStatus && this.geoComplyStatus.hint)
      return window.open(this.geoComplyStatus.hint.iosWakeupUrl);
    return window.open(GeoComplyAppStores.ios);
  }

  public onVerifyInstallation(): any {
    this.supressErrorDialogue();
    if (this.oobeeInteraction) return this.verifyOobeeInstalation();
    return this.verifyInstallationContinue();
  }

  public verifyInstallationContinue(): any {
    this.jurisdiction.killConnection();
    this.funcToRetry = this.params["funcToRetry"];

    if (!this.funcToRetry) {
      this.funcToRetry = this.jurisdiction.getFuncToRetry();
    }

    return this.jurisdiction
      .verifyJurisdiction(this.funcToRetry)
      .then((geoResp: GeoResponse) => {
        if (geoResp.is_allowed) {
          this.installationVerified = true;
        }
      })
      .catch((geoResp: GeoResponse) => {
        console.log("Geo error recieved inside Compoeent", geoResp.error);
        this.downloadLink = null;
        this.processErrorMessaging(geoResp.error);
      });
  }

  private async verifyOobeeInstalation(): Promise<any> {
    console.log("Oobee Verification started");
    this.verifyLoader = await this.loadingController.create({
      message: "Please wait verifying ...",
      duration: 10000,
    });

    this.verifyLoader.present();

    this.funcToRetry = this.params["funcToRetry"];

    if (!this.funcToRetry) {
      this.funcToRetry = this.jurisdiction.getFuncToRetry();
    }

    let status: boolean;
    const getStatus = () => {
      status = this.geoComplyOobeeService.isGeolocationInProgress();
      console.log("geoStatus", status);
      if (status == false) {
        this.oobeeContinue();
        clearInterval(this.myInterval);
      }
    };

    this.myInterval = setInterval(getStatus, 1000);
    setTimeout(() => clearInterval(this.myInterval), 30 * 1000);
  }

  private oobeeContinue() {
    if (this.jurisdiction.isAllowedJurisdiction) {
      this.verifyLoader.dismiss();
      this.installationVerified = true;
      return;
    }
    return this.jurisdiction
      .debounceVerifyJurisdiction(this.funcToRetry)
      .then((geoResp: GeoResponse) => {
        if (geoResp.is_allowed) {
          this.verifyLoader.dismiss();
          this.installationVerified = true;
        }
      })
      .catch((geoResp: GeoResponse) => {
        this.downloadLink = null;
        this.processErrorMessaging(geoResp.error);
      });
  }

  public onContinue() {
    var funcToRetry = this.jurisdiction.getFuncToRetry();
    if (funcToRetry) {
      setTimeout(() => {
        funcToRetry({
          is_allowed: this.jurisdiction.isAllowedJurisdiction(),
          address: "",
        });
      }, 300);
    }
    return this.navigator.toLobby();
  }

  private supressErrorDialogue() {
    return this.events.publish("event:geoComply:SuppressErrorDialog");
  }

  ngOnDestroy(): void {
    this.jurisdictionSubscription.unsubscribe();
  }
}
